'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const timeOptions = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'this_week' },
  { label: 'Last Week', value: 'last_week' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Custom Range', value: 'custom' },
];

export function LoyaltyRevenueEngine() {
  const [timeFilter, setTimeFilter] = useState('this_week');

  const selectedTimeLabel =
    timeOptions.find(option => option.value === timeFilter)?.label ||
    'This Week';

  return (
    <div className='space-y-6'>
      {/* Section Header */}
      <div className='flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0'>
        <div>
          <h2 className='text-xl font-bold'>Loyalty Revenue Engine</h2>
          <p className='text-md text-gray-500'>
            Track your loyalty program performance and revenue impact
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='outline' className='h-9 sm:h-10 gap-1 sm:gap-2'>
              {selectedTimeLabel}
              <ChevronDown className='h-3.5 w-3.5 sm:h-4 sm:w-4 opacity-50' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-[180px]'>
            {timeOptions.map(option => (
              <DropdownMenuItem
                key={option.value}
                className='cursor-pointer text-md'
                onClick={() => setTimeFilter(option.value)}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Cards Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch'>
        {/* Card 1: Total Loyalty Members */}
        <div className='bg-white rounded-2xl border p-4 flex flex-col justify-between min-h-[140px]'>
          <div className='space-y-1'>
            <div className='text-md font-medium text-gray-600'>
              Total Loyalty Members
            </div>
            <div className='text-2xl font-bold text-gray-900'>4,321</div>
          </div>
          <div className='text-md text-gray-500 mt-2'>+142 this week</div>
        </div>

        {/* Card 2: Revenue from Loyalty */}
        <div className='bg-white rounded-2xl border p-4 flex flex-col justify-between min-h-[140px]'>
          <div className='space-y-1'>
            <div className='text-md font-medium text-gray-600'>
              Revenue from Loyalty (30d)
            </div>
            <div className='text-2xl font-bold text-gray-900'>$23,750</div>
          </div>
          <div className='text-md text-secondary mt-2'>
            +12.5% from last month
          </div>
        </div>

        {/* Card 3: Repeat Purchase Rate */}
        <div className='bg-white rounded-2xl border p-4 flex flex-col justify-between min-h-[140px]'>
          <div className='space-y-1'>
            <div className='text-md font-medium text-gray-600'>
              Repeat Purchase Rate
            </div>
            <div className='text-2xl font-bold text-gray-900'>47%</div>
          </div>
          <div className='mt-2'>
            <div className='w-full bg-gray-200 rounded-full h-2'>
              <div
                className='h-2 rounded-full bg-secondary'
                style={{ width: '47%' }}
              ></div>
            </div>
          </div>
        </div>

        {/* Card 4: Avg. Order Value Comparison */}
        <div className='bg-white rounded-2xl border p-4 flex flex-col justify-between min-h-[140px]'>
          <div className='space-y-1'>
            <div className='text-md font-medium text-gray-600'>
              Avg. Order Value Comparison
            </div>
          </div>
          <div className='flex items-end justify-center space-x-4 mt-2 mb-2'>
            <div className='flex flex-col items-center'>
              <div
                className='w-6 bg-secondary rounded-t'
                style={{ height: '40px' }}
              ></div>
              <div className='text-xs text-gray-600 mt-1'>Loyalty</div>
            </div>
            <div className='flex flex-col items-center'>
              <div
                className='w-6 bg-gray-300 rounded-t'
                style={{ height: '24px' }}
              ></div>
              <div className='text-xs text-gray-600 mt-1'>Non-Loyalty</div>
            </div>
          </div>
          <div className='space-y-1'>
            <div className='flex items-center text-xs'>
              <div className='w-2 h-2 bg-secondary rounded-full mr-2'></div>
              <span>Loyalty: $68.45</span>
            </div>
            <div className='flex items-center text-xs'>
              <div className='w-2 h-2 bg-gray-300 rounded-full mr-2'></div>
              <span>Non-Loyalty: $41.20</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='flex justify-end'>
        <Button
          variant='link'
          className='text-secondary font-medium text-md p-0 h-auto flex items-center'
        >
          See more loyalty reports
          <ChevronRight className='h-4 w-4 ml-1' />
        </Button>
      </div>
    </div>
  );
}
